import React, { useRef, useState, useEffect } from 'react';
import { motion, useMotionTemplate, useMotionValue, useSpring } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { Play, Users, Lock, Scale, Sparkles, ChevronDown } from 'lucide-react';
import { useVisitorCount } from '../hooks/useVisitorCount';
import { useClient } from '../contexts/ClientContext';
import { Link } from 'react-router-dom';

const WelcomeAnimation = () => {
    const navigate = useNavigate();
    const clientCtx = useClient();
    const clearActiveClient = clientCtx?.clearActiveClient;
    const counts = useVisitorCount();
    const [caseDropdownOpen, setCaseDropdownOpen] = useState(false);

    const handleGuestClick = () => {
        if (clearActiveClient) clearActiveClient();
        navigate('/return-selection');
    };

    return (
        <div className="min-h-screen relative flex flex-col justify-between overflow-hidden bg-slate-900">
            {/* 1. Base Liquid Mesh Background (Deep & Abstract) */}
            <div className="absolute inset-0 z-0">
                <div className="absolute top-0 left-0 w-full h-full bg-[radial-gradient(circle_at_50%_50%,rgba(17,24,39,1),rgba(0,0,0,1))]"></div>
                <div className="absolute top-[-10%] left-[-10%] w-[50%] h-[50%] bg-purple-600 rounded-full mix-blend-multiply filter blur-[128px] opacity-40 animate-blob"></div>
                <div className="absolute top-[-10%] right-[-10%] w-[50%] h-[50%] bg-cyan-600 rounded-full mix-blend-multiply filter blur-[128px] opacity-40 animate-blob animation-delay-2000"></div>
                <div className="absolute bottom-[-10%] left-[20%] w-[50%] h-[50%] bg-pink-600 rounded-full mix-blend-multiply filter blur-[128px] opacity-40 animate-blob animation-delay-4000"></div>

                {/* Noise Texture Overlay for realism */}
                <div className="absolute inset-0 opacity-[0.03] pointer-events-none" style={{ backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noiseFilter'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.65' numOctaves='3' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noiseFilter)'/%3E%3C/svg%3E")` }}></div>
            </div>

            {/* Top Navigation Bar on Front Page */}
            {/* Top Bar with ONLY the Top Corner Case Laws Button */}
            <header className="relative z-30 w-full px-6 py-4 flex items-center justify-between border-b border-white/10 backdrop-blur-md bg-black/20">
                <div className="flex items-center gap-2">
                    <span className="text-xl font-bold tracking-tight text-white">Tax Eases</span>
                </div>

                <div className="relative">
                    {/* ONLY Top Corner Case Law Button */}
                    <button
                        onClick={() => setCaseDropdownOpen(!caseDropdownOpen)}
                        onMouseEnter={() => setCaseDropdownOpen(true)}
                        className="flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-bold text-cyan-300 hover:text-white bg-cyan-500/10 hover:bg-cyan-500/20 transition border border-cyan-400/40 shadow-sm shadow-cyan-500/20"
                    >
                        <Scale className="w-4 h-4 text-cyan-400" />
                        Case Laws
                        <ChevronDown className="w-4 h-4" />
                    </button>

                    {caseDropdownOpen && (
                        <div
                            onMouseLeave={() => setCaseDropdownOpen(false)}
                            className="absolute right-0 mt-2 w-60 rounded-xl bg-slate-900/98 border border-cyan-500/40 shadow-2xl backdrop-blur-2xl p-2 z-50 animate-in fade-in slide-in-from-top-2"
                        >
                            <Link
                                to="/case-laws"
                                onClick={() => setCaseDropdownOpen(false)}
                                className="flex items-center gap-2.5 px-3.5 py-2.5 rounded-lg text-xs font-semibold text-slate-200 hover:bg-cyan-500/20 hover:text-cyan-300 transition"
                            >
                                <Sparkles className="w-4 h-4 text-cyan-400" />
                                All Case Laws Hub
                            </Link>
                            <Link
                                to="/case-laws/income-tax"
                                onClick={() => setCaseDropdownOpen(false)}
                                className="flex items-center gap-2.5 px-3.5 py-2.5 rounded-lg text-xs font-semibold text-slate-200 hover:bg-cyan-500/20 hover:text-cyan-300 transition"
                            >
                                <Scale className="w-4 h-4 text-cyan-400" />
                                Income Tax Judgments
                            </Link>
                            <Link
                                to="/case-laws/gst"
                                onClick={() => setCaseDropdownOpen(false)}
                                className="flex items-center gap-2.5 px-3.5 py-2.5 rounded-lg text-xs font-semibold text-slate-200 hover:bg-cyan-500/20 hover:text-cyan-300 transition"
                            >
                                <span className="w-4 h-4 flex items-center justify-center font-bold text-teal-400 text-[10px] bg-teal-950/80 rounded border border-teal-500/30">GST</span>
                                GST Rulings & Orders
                            </Link>
                        </div>
                    )}
                </div>
            </header>

            {/* 2. Main Content Container (Original 2 Buttons Only) */}
            <motion.div
                initial={{ opacity: 0, scale: 0.9, y: 20 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                transition={{ duration: 0.8, ease: "easeOut" }}
                className="relative z-10 flex flex-col items-center justify-center gap-12 my-auto py-8 px-4"
            >
                {/* Title */}
                <div className="text-center space-y-2">
                    <h1 className="text-5xl md:text-7xl font-bold tracking-tight text-transparent bg-clip-text bg-gradient-to-br from-white via-white/90 to-white/50 drop-shadow-lg">
                        Tax Eases
                    </h1>
                    <p className="text-lg md:text-xl text-white/60 font-medium tracking-wide">
                        Simplify Your Returns
                    </p>
                </div>

                {/* The Original 2 Liquid Buttons Only */}
                <div className="flex flex-col md:flex-row gap-6 mt-4">
                    <LiquidButton
                        onClick={handleGuestClick}
                        text="Continue as Guest"
                        Icon={Users}
                    />
                    <LiquidButton
                        onClick={() => navigate('/premium-auth')}
                        text="Login as Premium"
                        Icon={Lock}
                    />
                </div>

            </motion.div>

            {/* Total Users at the Bottom */}
            {counts.total !== null && (
                <motion.div 
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 1, duration: 0.8 }}
                    className="absolute bottom-14 left-0 right-0 z-20 flex justify-center"
                >
                    <div className="flex items-center gap-2 text-white/70 bg-white/5 border border-white/10 px-6 py-2 rounded-full backdrop-blur-md shadow-2xl hover:bg-white/10 transition-all group">
                        <Users size={14} className="text-cyan-400 opacity-70 group-hover:opacity-100" />
                        <span className="text-[10px] font-bold tracking-[0.2em] uppercase opacity-80 group-hover:opacity-100 italic">Total Users: <span className="text-white text-xs ml-1 not-italic font-mono">{counts.total}</span></span>
                    </div>
                </motion.div>
            )}

            {/* Contact / Support Footer Strip */}
            <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 1.2, duration: 0.8 }}
                className="absolute bottom-0 left-0 right-0 z-20 flex items-center justify-center py-2.5 px-4 bg-black/40 backdrop-blur-sm border-t border-white/10"
            >
                <div className="flex items-center gap-2 text-sm text-white/50">
                    <svg xmlns="http://www.w3.org/2000/svg" className="w-3.5 h-3.5 text-cyan-400 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                    </svg>
                    <span>Need help?</span>
                    <span className="text-white/20">•</span>
                    <span>Contact us:</span>
                    <a
                        href="mailto:admin@taxeases.com"
                        className="text-cyan-400 hover:text-cyan-300 font-semibold transition-colors duration-200 hover:underline underline-offset-2"
                    >
                        admin@taxeases.com
                    </a>
                </div>
            </motion.div>
        </div>
    );
};

/* 
 * Animated Liquid Button Component 
 * Recreates the vibrant, contained liquid effect from the image 
 */
const LiquidButton = ({ onClick, text, Icon = Play }) => {
    const ROTATION_RANGE = 32.5;
    const HALF_ROTATION_RANGE = 32.5 / 2;

    const ref = useRef(null);

    const x = useMotionValue(0);
    const y = useMotionValue(0);

    const xSpring = useSpring(x);
    const ySpring = useSpring(y);

    const transform = useMotionTemplate`rotateX(${xSpring}deg) rotateY(${ySpring}deg)`;

    const handleMouseMove = (e) => {
        if (!ref.current) return;

        const rect = ref.current.getBoundingClientRect();

        const width = rect.width;
        const height = rect.height;

        const mouseX = (e.clientX - rect.left) * ROTATION_RANGE;
        const mouseY = (e.clientY - rect.top) * ROTATION_RANGE;

        const rX = (mouseY / height - HALF_ROTATION_RANGE) * -1;
        const rY = mouseX / width - HALF_ROTATION_RANGE;

        x.set(rX);
        y.set(rY);
    };

    const handleMouseLeave = () => {
        x.set(0);
        y.set(0);
    };

    return (
        <motion.button
            ref={ref}
            onMouseMove={handleMouseMove}
            onMouseLeave={handleMouseLeave}
            onClick={onClick}
            style={{
                transformStyle: "preserve-3d",
                transform,
            }}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            className="group relative h-20 w-72 rounded-[2.5rem] bg-black/20 backdrop-blur-md transition-all duration-300 ease-out"
        >
            {/* A. The "Liquid" Gradient Background inside the button */}
            <div
                className="absolute inset-0 rounded-[2.5rem] opacity-80 group-hover:opacity-100 transition-opacity duration-500 overflow-hidden"
            >
                {/* Moving mesh gradient */}
                <div className="absolute inset-[-50%] w-[200%] h-[200%] bg-gradient-to-br from-indigo-500 via-purple-500 to-pink-500 animate-mesh filter blur-xl opacity-90"></div>

                {/* Inner light reflection (Top) */}
                <div className="absolute top-0 left-0 w-full h-1/2 bg-gradient-to-b from-white/40 to-transparent opacity-60"></div>
            </div>

            {/* B. Glass Border / Bevel Effect */}
            <div className="absolute inset-0 rounded-[2.5rem] border border-white/20 shadow-[inset_0_0_20px_rgba(255,255,255,0.2)]"></div>

            {/* C. Content Layer */}
            <div
                style={{ transform: "translateZ(20px)" }}
                className="relative z-10 flex h-full w-full items-center justify-center gap-3 text-xl font-bold text-white drop-shadow-md"
            >
                <div className="p-2 bg-white/20 rounded-full backdrop-blur-sm border border-white/30 shadow-inner">
                    <Icon className="fill-white w-5 h-5 ml-0.5" />
                </div>
                <span>{text}</span>
            </div>

            {/* D. Bottom Glow reflection */}
            <div className="absolute -bottom-4 left-10 right-10 h-4 bg-purple-500/50 blur-lg rounded-[100%] opacity-0 group-hover:opacity-60 transition-opacity duration-300"></div>
        </motion.button>
    );
};

export default WelcomeAnimation;
